import { useTranslation } from 'react-i18next';
import type { ProgressComparisonEntry } from '@/api/progress/progressApi.types';
import { Badge } from '@/components/ui/badge';
import { formatIsoDateToDayAndMonth } from '@/utils/dateFormatting';
import { formatProgressNumber, formatSignedProgressDelta, resolveComparisonBadgeTone } from './progressFormatting';

interface BeforeAfterComparisonProps {
  entries: ProgressComparisonEntry[];
}

export function BeforeAfterComparison({ entries }: BeforeAfterComparisonProps) {
  const { t } = useTranslation('progress');

  if (entries.length === 0) {
    return null;
  }

  return (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-wider text-zoomuted mb-2">
        {t('comparison.title')}
      </h3>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {entries.map(function renderTile(entry) {
          const unitLabel =
            entry.scale.unitCode === null ? t('unit.rating') : t('unit.' + entry.scale.unitCode);
          return (
            <div
              key={entry.metricCode}
              className="rounded-xl border border-zooborder p-3 flex flex-col gap-2"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-bold text-zooindigo">
                  {t('metric.' + entry.metricCode)}
                </span>
                <Badge tone={resolveComparisonBadgeTone(entry.classification)}>
                  {t('comparison.' + entry.classification)}
                </Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <div>
                  <p className="text-[10px] text-zoomuted uppercase tracking-wider">
                    {t('comparison.baseline')}
                  </p>
                  <p className="font-semibold text-zooindigo">
                    {formatProgressNumber(entry.baselineValue)} {unitLabel}
                  </p>
                  <p className="text-[10px] text-zoomuted">
                    {formatIsoDateToDayAndMonth(entry.baselineRecordedOn)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-zoomuted uppercase tracking-wider">
                    {t('comparison.latest')}
                  </p>
                  <p className="font-semibold text-zooindigo">
                    {formatProgressNumber(entry.latestValue)} {unitLabel}
                  </p>
                  <p className="text-[10px] text-zoomuted">
                    {formatIsoDateToDayAndMonth(entry.latestRecordedOn)}
                  </p>
                </div>
              </div>
              <p className="text-xs font-semibold text-zoomuted">
                {formatSignedProgressDelta(entry.delta)} {unitLabel}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
